import { useEffect, useState } from 'react';
import type { MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { ListaMisPedidos, MiPedido, OperacionesMisEntradas } from '../api/ops/misEntradas';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import { Menu, type OpcionMenu } from '../componentes/Menu';
import { Selector } from '../componentes/Selector';
import { Ventana } from '../componentes/Ventana';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { haySiguiente, usePaginacionCursor } from '../logica/paginacion';
import { RUTAS_CLIENTE } from '../rutas';
import { AvisoPc, AvisoTituladoPc, BotonPc, Pagina } from './Pagina';
import './pases.css';
import type { IdiomaSalida } from '../api/ops/misEntradas';
import { type Banda, cuantasEntradas, ESTADO_PEDIDO, ESTADO_PEDIDO_EN, MIS_ENTRADAS, notaPedido } from './textosEntradas';

/**
 * «Mis entradas» (10.15, `/mis-entradas`): con el token de cliente de «Mis datos» (2.9), el negocio a elegir entre sus
 * perfiles (`listMyBusinessProfiles`) y sus pedidos ahí (`listMyTicketOrders`, Próximas · Pasadas), con sus entradas y el
 * estado. Tras un cambio real de fecha y dentro del plazo (`can_cancel`, `free_cancellation_until`, G17, G20): «Mantener»
 * o «Cancelar gratis» (`cancelMyTicketOrder`; `409` si el plazo ya pasó). «Anterior / Siguiente» (G121). Pie G195.
 *
 * Un pedido pendiente de pago trae `payment_url` y `expires_at` (G833): «Paga ahora» abre el pago y «Caduca a las…» dice
 * hasta cuándo se guardan las entradas; si se anula sin pagar, no se cobra nada.
 *
 * «Lo próximo» (patrón de las páginas «Mis…»): en Próximas, el primer pedido va grande arriba con un solo botón («Paga
 * ahora» si falta pagar; si no, «Ver mis pases», hacia 6.7) y lo demás en «⋯» (RV Menu): «Reenviar el pase»
 * (`resendMyPass` de cada `Ticket.pass_id`, G787) y «Cancelar gratis». El resto, en «Más adelante». Sin «Puertas a las…»:
 * el contrato no trae la hora de puertas.
 */
const fecha = (iso: string, zona: string | undefined, opciones: Intl.DateTimeFormatOptions, locale = 'es-ES') => {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : f.toLocaleString(locale, { ...opciones, timeZone: zona });
};

interface Props {
    ops: OperacionesClienteFinal;
    entradas: OperacionesMisEntradas;
    onSalir: () => void;
    onCaducada: () => void;
    movil?: boolean;
    /** G928: por la ruta (`?lang=en`) o el navegador, como 10.16. */
    idioma?: IdiomaSalida;
}

export function MisEntradas({ ops, entradas, onSalir, onCaducada, movil = true, idioma = 'es' }: Props) {
    const t = MIS_ENTRADAS[idioma];
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [error, setError] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [cuando, setCuando] = useState<'upcoming' | 'past'>('upcoming');
    const [lista, setLista] = useState<{ clave: string; datos: ListaMisPedidos | null; error: boolean } | null>(null);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [cancelar, setCancelar] = useState<MiPedido | null>(null);
    const [cancelando, setCancelando] = useState(false);
    const [reenviando, setReenviando] = useState<string | null>(null);
    // El reenvío y la cancelación llevan título y texto aparte (la banda del dibujo 10.15, G1203); lo demás, el aviso de una línea.
    const [aviso, setAviso] = useState<{ texto: string; tipo: 'error' | 'info' } | (Banda & { tipo: 'hecho' | 'cuidado' }) | null>(null);
    const [intento, setIntento] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (vivo) {
                    setPerfiles(l.data.filter((p) => p.ended == null));
                    setError(false);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setError(true);
                }
            });
        return () => {
            vivo = false;
        };
        // onCaducada cambia en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${cuando}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        entradas.listMyTicketOrders(idPerfil, cuando, { cursor: pag.cursor, limit: 20 })
            .then((datos) => {
                if (vivo) {
                    setLista({ clave, datos, error: false });
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setLista({ clave, datos: null, error: true });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entradas, idPerfil, cuando, pag.cursor, clave]);

    const nombre = perfil?.business.name ?? '';
    const reintentar = () => { setIntento((i) => i + 1); };

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada idioma={idioma}>
                <section className="rv-pc-card" lang={idioma}>
                    <div className="rv-pc-card__cab"><h1>{t.errTitulo}</h1><span>{t.errTexto}</span></div>
                    <BotonPc texto={t.reintentar} icono="refresh-cw" onClick={reintentar} />
                </section>
            </Pagina>
        );
    }

    const actual = lista?.clave === clave ? lista : null;
    const pedidos = actual?.datos?.data ?? null;
    const cargando = perfiles === null || (perfil !== null && actual === null);

    const confirmarCancelacion = () => {
        if (cancelar === null || idPerfil === null) {
            return;
        }
        setCancelando(true);
        const pedido = cancelar;
        entradas.cancelMyTicketOrder(idPerfil, pedido.id)
            .then(() => {
                setCancelar(null);
                setAviso({ tipo: 'hecho', ...t.cancelado(pedido.tickets.length, pedido.refund_if_cancelled != null ? dinero(pedido.refund_if_cancelled) : null) });
                reintentar();
            })
            .catch((e: unknown) => {
                setCancelar(null);
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else if (e instanceof ErrorApi && e.estado === 409) {
                    const hasta = pedido.free_cancellation_until != null ? fecha(pedido.free_cancellation_until, pedido.event.timezone, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }, t.locale) : '';
                    setAviso({ tipo: 'cuidado', ...t.plazoAcabado(hasta === '' ? null : hasta) });
                    reintentar();
                } else {
                    setAviso({ tipo: 'cuidado', ...t.noCancelado });
                }
            })
            .finally(() => { setCancelando(false); });
    };

    const reenviar = (p: MiPedido) => {
        const ids = pasesDe(p);
        if (idPerfil === null || ids.length === 0 || reenviando !== null) {
            return;
        }
        setReenviando(p.id);
        setAviso(null);
        Promise.all(ids.map((id) => ops.resendMyPass(idPerfil, id)))
            .then((rs) => {
                const quedan = rs.map((r) => r.remaining_this_hour).filter((x): x is number => x !== undefined);
                // G1056: todos van al mismo cliente por el canal de la compra; el aviso dice cuál y a qué destino (enmascarado).
                setAviso({ tipo: 'hecho', ...t.reenviado(ids.length, quedan.length === 0 ? undefined : Math.min(...quedan), rs[0]?.channel, rs[0]?.destination_hint, nombre) });
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    // 10.15: el 429 y el `409 no_contact` con la banda amarilla del dibujo; el pase que ya no vale y la red, como antes.
                    if (e instanceof ErrorApi && e.estado === 429) {
                        setAviso({ tipo: 'cuidado', ...t.reenvioDemasiados(ids.length) });
                    } else if (e instanceof ErrorApi && e.estado === 409 && e.codigo === 'no_contact') {
                        setAviso({ tipo: 'cuidado', ...t.reenvioSinContacto(ids.length) });
                    } else {
                        setAviso({ tipo: 'error', texto: e instanceof ErrorApi && e.estado === 409 ? t.reenvioNo : t.reenvioError });
                    }
                }
            })
            .finally(() => { setReenviando(null); });
    };
    const alternar = (id: string) => { setAbierto((a) => (a === id ? null : id)); };
    const acciones = (p: MiPedido): AccionesPedido => ({
        reenviar: () => { reenviar(p); },
        reenviando: reenviando === p.id,
        verPases: () => { navegador.ir(RUTAS_CLIENTE.misPases); },
        cancelar: () => { setAviso(null); setCancelar(p); },
    });
    const principal = cuando === 'upcoming' && pedidos !== null ? (pedidos[0] ?? null) : null;
    const resto = principal === null ? pedidos : (pedidos ?? []).slice(1);

    return (
        <Pagina
            cabecera={perfil === null ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre, linea: t.titulo }}
            pie={perfil === null ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre, privacidad: perfil.business.privacy_url ?? null }}
            onSalir={onSalir}
            idioma={idioma}
            titulo={{ titulo: t.titulo, sub: t.sub(nombre) }}
        >
            {perfiles !== null && perfiles.length > 1 && (
                // G996: el negocio con la pieza común RV Selector, en el idioma de la página («Search», «No results for…»).
                <Selector
                    etiqueta={t.negocio}
                    valor={perfil?.customer_id ?? ''}
                    opciones={perfiles.map((p) => ({ valor: p.customer_id, texto: p.business.name }))}
                    modo="lista"
                    idioma={idioma}
                    onChange={(v) => {
                        setElegido(v);
                        setAbierto(null);
                        reiniciar();
                    }}
                />
            )}
            <div className="rv-pc-paginas" role="tablist" aria-label={t.cuando}>
                {(['upcoming', 'past'] as const).map((c) => (
                    <button
                        key={c}
                        type="button"
                        role="tab"
                        aria-selected={cuando === c}
                        className={`rv-pc-b rv-pc-b--peq ${cuando === c ? 'rv-pc-b--pri' : 'rv-pc-b--sec'}`}
                        onClick={() => {
                            setCuando(c);
                            setAbierto(null);
                            reiniciar();
                        }}
                    >
                        <span>{c === 'upcoming' ? t.proximas : t.pasadas}</span>
                    </button>
                ))}
            </div>
            {aviso !== null && (aviso.tipo === 'hecho' || aviso.tipo === 'cuidado' ? <AvisoTituladoPc titulo={aviso.titulo} texto={aviso.texto} tipo={aviso.tipo} /> : <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />)}
            {!cargando && actual?.error !== true && principal !== null && (
                <ProximoPedido idioma={idioma} pedido={principal} abierto={abierto === principal.id} onAbrir={() => { alternar(principal.id); }} acciones={acciones(principal)} />
            )}
            {!cargando && actual?.error !== true && principal !== null && resto !== null && resto.length > 0 && <h2 className="rv-pc-mas">{t.masAdelante}</h2>}
            {(principal === null || (resto !== null && resto.length > 0) || cargando || actual?.error === true) && <section className="rv-pc-card rv-pc-card--seccion" aria-label={t.tusEntradas} lang={idioma} aria-busy={cargando}>
                {cargando ? (
                    <>
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 56, marginTop: 12 }} />
                    </>
                ) : actual?.error === true ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="circle-alert" /></span>
                        <strong>{t.errTitulo}</strong>
                        <BotonPc texto={t.reintentar} icono="refresh-cw" tipo="sec" peq onClick={reintentar} />
                    </div>
                ) : pedidos === null || pedidos.length === 0 ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="ticket" /></span>
                        <strong>{cuando === 'upcoming' ? t.vacioProximas : t.vacioPasadas}</strong>
                        <small>{t.vacioTexto}</small>
                    </div>
                ) : (
                    resto?.map((p) => (
                        <FilaPedido key={p.id} idioma={idioma} pedido={p} abierto={abierto === p.id} onAbrir={() => { alternar(p.id); }} acciones={acciones(p)} />
                    ))
                )}
            </section>}
            {actual?.datos != null && (pag.hayAnterior || haySiguiente(actual.datos.next_cursor)) && (
                <div className="rv-pc-paginas">
                    <BotonPc texto={t.anterior} icono="arrow-left" tipo="sec" peq desactivado={!pag.hayAnterior} onClick={pag.anterior} />
                    <BotonPc texto={t.siguiente} icono="arrow-right" tipo="sec" peq desactivado={!haySiguiente(actual.datos.next_cursor)} onClick={() => { pag.siguiente(actual.datos?.next_cursor ?? null); }} />
                </div>
            )}
            <Ventana
                abierta={cancelar !== null}
                titulo={t.ventanaTitulo}
                texto={t.ventanaTexto(cancelar?.refund_if_cancelled != null ? dinero(cancelar.refund_if_cancelled) : null)}
                cancelar={t.mantener}
                accion={t.cancelarGratis}
                accionIcono="calendar-x"
                ocupado={cancelando}
                ocupadoTexto={t.cancelando}
                movil={movil}
                idioma={idioma}
                onAccion={confirmarCancelacion}
                onCerrar={() => { setCancelar(null); }}
            />
        </Pagina>
    );
}

interface AccionesPedido {
    reenviar: () => void;
    reenviando: boolean;
    verPases: () => void;
    cancelar: () => void;
}

/** Los `pass_id` de las entradas válidas: lo que `resendMyPass` reenvía (uno por entrada). */
const pasesDe = (p: MiPedido): string[] => p.tickets.filter((e) => e.status === 'valid' && e.pass_id != null).map((e) => e.pass_id as string);

/** Pedido vigente con pases: confirmado, con la entrada válida y el evento sin cancelar. */
const conPases = (p: MiPedido) => p.status === 'confirmed' && p.event.status !== 'cancelled' && pasesDe(p).length > 0;

function CuerpoPedido({ pedido, idioma, acciones, id }: { pedido: MiPedido; idioma: IdiomaSalida; acciones: AccionesPedido | null; id: string }) {
    const t = MIS_ENTRADAS[idioma];
    const validas = pedido.tickets.filter((e) => e.status === 'valid').length;
    const zona = pedido.event.timezone;
    const nota = notaPedido(pedido.status, pedido.tickets.length, idioma);
    const vigente = conPases(pedido);
    return (
        <div id={id} className="rv-pc-pago__cuerpo">
            <div className="rv-pc-dato"><span>{t.localizador}</span><strong>{pedido.reference}</strong></div>
            {pedido.tickets.map((entrada) => (
                <div key={entrada.id} className="rv-pc-dato">
                    <span>{entrada.holder_name != null && entrada.holder_name !== '' ? `${t.entrada(entrada.seq)} · ${entrada.holder_name}` : t.entrada(entrada.seq)}</span>
                    <strong>{entrada.status === 'cancelled' ? t.anulada : pedido.status === 'pending_payment' ? t.sinPase : entrada.arrived_at != null ? t.usada : t.valida}</strong>
                </div>
            ))}
            <div className="rv-pc-dato"><span>{t.total}</span><strong>{dinero(pedido.total)}</strong></div>
            {nota !== null && <p className="rv-pc-nota"><Icono nombre={pedido.status === 'pending_payment' ? 'hourglass' : 'circle-check'} /><span>{nota}</span></p>}
            {pedido.status === 'pending_payment' && pedido.expires_at != null && (
                <p className="rv-pc-nota"><Icono nombre="clock" /><span>{t.caduca(fecha(pedido.expires_at, zona, { hour: '2-digit', minute: '2-digit' }, t.locale))}</span></p>
            )}
            {acciones !== null && pedido.status === 'pending_payment' && pedido.payment_url != null && (
                <a className="rv-pc-b rv-pc-b--pri" href={pedido.payment_url}><Icono nombre="credit-card" /><span>{t.pagar}</span></a>
            )}
            {pedido.status === 'confirmed' && validas > 0 && <p className="rv-pc-nota"><Icono nombre="mail" /><span>{t.pases}</span></p>}
            {acciones !== null && vigente && (
                <>
                    <BotonPc texto={t.verPases(pasesDe(pedido).length)} icono="qr-code" tipo="sec" onClick={acciones.verPases} />
                    <BotonPc texto={acciones.reenviando ? t.reenviando : t.reenviar(pasesDe(pedido).length)} icono="send" tipo="sec" desactivado={acciones.reenviando} onClick={acciones.reenviar} />
                </>
            )}
            {acciones !== null && pedido.can_cancel && (
                <>
                    <p className="rv-pc-nota">
                        <Icono nombre="calendar-x" />
                        <span>
                            {t.cambio(pedido.free_cancellation_until != null ? fecha(pedido.free_cancellation_until, zona, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }, t.locale) : null)}
                        </span>
                    </p>
                    <BotonPc texto={t.cancelarGratis} icono="calendar-x" tipo="sec" onClick={acciones.cancelar} />
                </>
            )}
        </div>
    );
}

/** «Lo próximo»: el primer pedido de Próximas, grande, con un solo botón y lo demás en «⋯» (RV Menu). */
function ProximoPedido({ pedido, abierto, onAbrir, acciones, idioma }: { pedido: MiPedido; abierto: boolean; onAbrir: () => void; acciones: AccionesPedido; idioma: IdiomaSalida }) {
    const t = MIS_ENTRADAS[idioma];
    const [, chip] = (idioma === 'en' ? ESTADO_PEDIDO_EN : ESTADO_PEDIDO)[pedido.status];
    const textoChip = (idioma === 'en' ? ESTADO_PEDIDO_EN : ESTADO_PEDIDO)[pedido.status][0];
    const zona = pedido.event.timezone;
    const n = pedido.tickets.length;
    const vigente = conPases(pedido);
    const dia = (opciones: Intl.DateTimeFormatOptions) => fecha(pedido.event.starts_at, zona, opciones, t.locale);
    const clave = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: zona });
    const [ahora] = useState(() => Date.now());
    const hoy = clave(new Date(ahora).toISOString());
    const evento = clave(pedido.event.starts_at);
    const manana = clave(new Date(ahora + 86_400_000).toISOString());
    const relativo = evento === hoy ? t.hoy : evento === manana ? t.manana : null;
    const diaCorto = relativo ?? dia({ weekday: 'short', day: 'numeric', month: 'short' });
    const hora = dia({ hour: '2-digit', minute: '2-digit' });
    const pendiente = pedido.status === 'pending_payment' && pedido.payment_url != null;
    const menu: OpcionMenu[] = [];
    if (vigente) {
        menu.push({ clave: 'reenviar', texto: acciones.reenviando ? t.reenviando : t.reenviar(pasesDe(pedido).length), icono: 'send', desactivada: acciones.reenviando, onClick: acciones.reenviar });
    }
    if (pedido.can_cancel) {
        menu.push({ clave: 'cancelar', texto: t.cancelarGratis, icono: 'calendar-x', peligro: true, onClick: acciones.cancelar });
    }
    const id = `rv-pc-pedido-${pedido.id}`;
    return (
        <section className="rv-pc-card rv-pc-proximo" aria-label={t.proximo}>
            <div className="rv-ps-pase__cab">
                <span className="rv-pc-ceja">{t.proximo}</span>
                {menu.length > 0 && <Menu etiqueta={t.masOpciones} fantasma idioma={idioma} opciones={menu} />}
            </div>
            <h2 className="rv-ps-pase__titulo">{t.aLas(diaCorto, hora)}</h2>
            <strong className="rv-ps-proximo__que">{`${pedido.event.name} · ${cuantasEntradas(n, idioma)}`}</strong>
            {relativo !== null && <span className="rv-ps-pase__cuando">{dia({ weekday: 'long', day: 'numeric', month: 'long' })}</span>}
            {(pedido.status !== 'confirmed' || pedido.can_cancel) && <span><Chip estado={pedido.can_cancel ? 'pending' : chip}>{pedido.can_cancel ? t.fechaCambiada : textoChip}</Chip></span>}
            {pendiente ? (
                <a className="rv-pc-b rv-pc-b--pri" href={pedido.payment_url ?? ''}><Icono nombre="lock" /><span>{`${t.pagar} · ${dinero(pedido.total)}`}</span></a>
            ) : vigente ? (
                <BotonPc texto={t.verPases(pasesDe(pedido).length)} icono="qr-code" onClick={acciones.verPases} />
            ) : null}
            <button type="button" className="rv-pc-enlace" aria-expanded={abierto} aria-controls={id} onClick={onAbrir}>
                <span>{abierto ? t.ocultarDetalles : t.verDetalles}</span><Icono nombre="chevron-down" />
            </button>
            {abierto && <CuerpoPedido pedido={pedido} idioma={idioma} acciones={null} id={id} />}
        </section>
    );
}

function FilaPedido({ pedido, abierto, onAbrir, acciones, idioma }: { pedido: MiPedido; abierto: boolean; onAbrir: () => void; acciones: AccionesPedido; idioma: IdiomaSalida }) {
    const t = MIS_ENTRADAS[idioma];
    const [texto, chip] = (idioma === 'en' ? ESTADO_PEDIDO_EN : ESTADO_PEDIDO)[pedido.status];
    const id = `rv-pc-pedido-${pedido.id}`;
    const zona = pedido.event.timezone;
    return (
        <div className="rv-pc-pago">
            <button type="button" className="rv-pc-pago__cab" aria-expanded={abierto} aria-controls={id} onClick={onAbrir}>
                <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre="ticket" /></span>
                <span>
                    <strong>{pedido.event.name}</strong>
                    <small>{`${fecha(pedido.event.starts_at, zona, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }, t.locale)} · ${cuantasEntradas(pedido.tickets.length, idioma)}`}</small>
                </span>
                <span className="rv-pc-pago__importe">
                    <strong>{dinero(pedido.total)}</strong>
                    {pedido.status !== 'confirmed' && <Chip estado={chip}>{texto}</Chip>}
                    {pedido.can_cancel && <Chip estado="pending">{t.fechaCambiada}</Chip>}
                </span>
                <Icono nombre="chevron-down" />
            </button>
            {abierto && <CuerpoPedido pedido={pedido} idioma={idioma} acciones={acciones} id={id} />}
        </div>
    );
}
